import React, { useEffect, useState } from "react";
import { Mail, Eye, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { log } from "../lib/log";
import { Switch } from "./ui/switch";

function DigestPreviewModal({ onClose, email }) {
  const [html, setHtml] = useState(null);
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState(email || "");
  useEffect(() => {
    api.get("/digest/preview").then((r) => setHtml(r.data.html)).catch((e) => { log.warn("digest preview failed:", e); toast.error("Could not build preview"); });
    api.get("/digest/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);
  const sendTest = async () => {
    setSending(true);
    try {
      const r = await api.post("/digest/send-test", { to: to.trim() || null });
      toast.success(`Digest sent to ${r.data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6 bg-black/70 backdrop-blur-sm" data-testid="digest-preview-modal" onClick={onClose}>
      <div className="relative w-full max-w-2xl h-[92vh] sm:h-[88vh] rounded-2xl border border-[#C8A96E]/60 bg-[#15111A] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-white/10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#C8A96E]">Monday Digest Preview</div>
            <div className="text-[11px] text-[#A89C98] truncate" data-testid="digest-config-status">
              {status ? (status.configured ? "Email delivery active · sends Mondays 08:00 UTC" : "Email delivery not configured yet — preview only") : "…"}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <input
              data-testid="digest-send-to-input"
              type="email"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="you@email.com"
              className="hidden sm:block w-44 bg-transparent border-b border-[#A89C98]/40 focus:border-[#C8A96E] outline-none text-xs text-white py-1.5"
            />
            <button data-testid="digest-send-test-btn" onClick={sendTest} disabled={sending || !status?.configured} className="flex items-center gap-1.5 border border-[#C8A96E]/60 text-[#C8A96E] hover:bg-[#C8A96E]/10 disabled:opacity-40 rounded-full px-3 py-1.5 text-[10px] uppercase tracking-widest">
              <Send size={12} /> {sending ? "Sending…" : "Send me a test"}
            </button>
            <button data-testid="digest-preview-close" onClick={onClose} className="text-[#A89C98] hover:text-white p-1"><X size={18} /></button>
          </div>
        </div>
        {html ? (
          <iframe title="digest" srcDoc={html} sandbox="" className="flex-1 w-full bg-[#15111A]" data-testid="digest-preview-frame" />
        ) : (
          <div className="flex-1 flex items-center justify-center text-[#A89C98] text-sm">Composing your digest…</div>
        )}
      </div>
    </div>
  );
}

export default function DigestSettings() {
  const [enabled, setEnabled] = useState(true);
  const [email, setEmail] = useState("");
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    api.get("/digest/preferences").then((r) => { setEnabled(r.data.weekly_digest); setEmail(r.data.email || ""); }).catch((e) => log.warn("digest prefs load failed:", e));
  }, []);

  const toggle = async (val) => {
    setEnabled(val);
    try {
      await api.patch("/digest/preferences", { weekly_digest: val });
      toast.success(val ? "Weekly digest on — see you Monday" : "Weekly digest paused");
    } catch (e) {
      setEnabled(!val);
      toast.error("Could not save preference");
    }
  };

  return (
    <section className="mt-10" data-testid="digest-settings">
      <div className="glass rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
        <div className="w-11 h-11 rounded-full bg-[#4A3356]/50 border border-[#C8A96E]/40 flex items-center justify-center shrink-0">
          <Mail size={18} className="text-[#C8A96E]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A89C98]">Weekly Cosmic Digest</div>
          <div className="font-heading text-lg sm:text-xl mt-1">Monday morning in your inbox</div>
          <p className="text-xs sm:text-sm text-[#A89C98] mt-1 leading-snug">
            Rare transits over your saved cities + your three best-aligned cities, every Monday{email ? ` → ${email}` : ""}.
          </p>
        </div>
        <div className="flex items-center gap-3 sm:gap-4 shrink-0">
          <button data-testid="digest-preview-btn" onClick={() => setPreview(true)} className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#C8A96E] border border-[#C8A96E]/40 hover:bg-[#C8A96E]/10 rounded-full px-3 py-2">
            <Eye size={12} /> Preview
          </button>
          <div className="flex items-center gap-2">
            <Switch data-testid="digest-toggle" checked={enabled} onCheckedChange={toggle} className="data-[state=checked]:bg-[#C8A96E]" />
            <span className="text-[10px] uppercase tracking-widest text-[#A89C98] w-8" data-testid="digest-toggle-label">{enabled ? "On" : "Off"}</span>
          </div>
        </div>
      </div>
      {preview && <DigestPreviewModal onClose={() => setPreview(false)} email={email} />}
    </section>
  );
}
